import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

/** Response from GET /dashboard/billing/usage */
export interface BillingUsageResponse {
  tier: string
  subscription_status: string | null
  billing_interval: string | null
  current_period: {
    start: string | null
    end: string | null
  }
  emails: {
    sent: number
    included: number
    remaining: number
    overage: number
  }
  overage_policy: {
    strikes: number
    acknowledged: boolean
  }
  marketing: {
    tier: string
    sent: number
    period: {
      start: string | null
      end: string | null
    }
  }
}

/** Response from POST /dashboard/billing/acknowledge-overage */
interface AcknowledgeOverageResponse {
  acknowledged: boolean
  message: string
}

const USAGE_KEY = ['billing-usage'] as const

export function useBillingUsage(): ReturnType<typeof useQuery<BillingUsageResponse>> {
  return useQuery({
    queryKey: [...USAGE_KEY],
    queryFn: () => apiClient.get<BillingUsageResponse>(endpoints.billing.usage),
  })
}

export function useAcknowledgeOverage(): ReturnType<
  typeof useMutation<AcknowledgeOverageResponse, unknown, void>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<AcknowledgeOverageResponse>(endpoints.billing.acknowledgeOverage, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...USAGE_KEY] })
      qc.invalidateQueries({ queryKey: ['account'] })
    },
  })
}
